/* Exercise identity: renaming or merging an exercise name across everything
 * that refers to it by name. Logs, programs, prescriptions and exerciseMeta
 * are keyed by the logged name, so a rename that only touched the plan would
 * split history, PRs and charts in two. Plan exercises keep their ids; setCounts
 * and swaps keys are ids, so only swap values (names) change.
 * Python twin: src/fitlogic/names.py (same vectors in vectors/names.json). */

const isObj = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const own = (o, k) => (isObj(o) && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined);

function hasDoneSet(sets) {
  return Array.isArray(sets) && sets.some(s => isObj(s) && s.done);
}

/**
 * What a rename/merge of `from` into `to` would touch.
 * @param {object} state fitness state
 * @param {string} from name to fold away
 * @param {string} to name to keep
 * @returns {{dates: string[], overlapping: string[], fromSessions: number, toSessions: number, planDays: string[]}}
 *   dates: dates with sets under `from`; overlapping: dates that also have `to`
 *   (their sets get concatenated, `to` first); sessions count dates with a done set.
 */
export function renamePreview(state, from, to) {
  const logs = isObj(state && state.logs) ? state.logs : {};
  const dates = [];
  const overlapping = [];
  let fromSessions = 0;
  let toSessions = 0;
  for (const date of Object.keys(logs).sort()) {
    const log = logs[date];
    if (!isObj(log)) continue;
    const f = own(log, from);
    const t = own(log, to);
    if (f !== undefined) dates.push(date);
    if (f !== undefined && t !== undefined) overlapping.push(date);
    if (hasDoneSet(f)) fromSessions += 1;
    if (hasDoneSet(t)) toSessions += 1;
  }
  const planDays = [];
  for (const day of Array.isArray(state && state.plan) ? state.plan : []) {
    if (isObj(day) && Array.isArray(day.exercises) && day.exercises.some(e => isObj(e) && e.name === from)) {
      planDays.push(day.name || '');
    }
  }
  return { dates, overlapping, fromSessions, toSessions, planDays };
}

/**
 * Fold every reference to `from` into `to`. Pure: returns a new state that
 * shares untouched parts with the input. Records the rename in
 * exerciseAliases so typing the old name resolves to the new one.
 * @param {object} state fitness state
 * @param {string} from
 * @param {string} to
 * @returns {object} new state (the input itself when there is nothing to do)
 */
export function renameExerciseEverywhere(state, from, to) {
  if (!isObj(state) || typeof from !== 'string' || typeof to !== 'string') return state;
  from = from.trim();
  to = to.trim();
  if (!from || !to || from === to) return state;
  const next = { ...state };

  if (isObj(state.logs)) {
    const logs = {};
    for (const [date, log] of Object.entries(state.logs)) {
      if (!isObj(log) || own(log, from) === undefined) { logs[date] = log; continue; }
      const moved = Array.isArray(log[from]) ? log[from] : [];
      const out = {};
      for (const [name, sets] of Object.entries(log)) {
        if (name === from) {
          if (own(log, to) === undefined) out[to] = moved;
        } else if (name === to) {
          out[to] = [...(Array.isArray(sets) ? sets : []), ...moved];
        } else {
          out[name] = sets;
        }
      }
      logs[date] = out;
    }
    next.logs = logs;
  }

  if (Array.isArray(state.plan)) {
    next.plan = state.plan.map(day => (isObj(day) && Array.isArray(day.exercises)
      && day.exercises.some(e => isObj(e) && e.name === from)
      ? { ...day, exercises: day.exercises.map(e => (isObj(e) && e.name === from ? { ...e, name: to } : e)) }
      : day));
  }

  if (isObj(state.swaps)) {
    const swaps = {};
    for (const [date, map] of Object.entries(state.swaps)) {
      if (!isObj(map) || !Object.values(map).includes(from)) { swaps[date] = map; continue; }
      swaps[date] = Object.fromEntries(Object.entries(map).map(([id, name]) => [id, name === from ? to : name]));
    }
    next.swaps = swaps;
  }

  if (Array.isArray(state.customExercises) && state.customExercises.includes(from)) {
    next.customExercises = state.customExercises.filter(n => n !== from);
  }

  if (isObj(state.exerciseMeta) && own(state.exerciseMeta, from) !== undefined) {
    const meta = { ...state.exerciseMeta };
    if (own(meta, to) === undefined) meta[to] = meta[from];
    delete meta[from];
    next.exerciseMeta = meta;
  }

  if (Array.isArray(state.programs) && state.programs.some(p => isObj(p) && p.exercise === from)) {
    next.programs = state.programs.map(p => (isObj(p) && p.exercise === from ? { ...p, exercise: to } : p));
  }

  if (isObj(state.prescriptions)) {
    const presc = {};
    for (const [date, map] of Object.entries(state.prescriptions)) {
      if (!isObj(map) || own(map, from) === undefined) { presc[date] = map; continue; }
      const out = { ...map };
      if (own(out, to) === undefined) out[to] = out[from];
      delete out[from];
      presc[date] = out;
    }
    next.prescriptions = presc;
  }

  const aliases = {};
  for (const [old, cur] of Object.entries(isObj(state.exerciseAliases) ? state.exerciseAliases : {})) {
    if (old === to) continue; // `to` is the live name now; an alias away from it would loop back
    aliases[old] = cur === from ? to : cur;
  }
  aliases[from] = to;
  next.exerciseAliases = aliases;
  return next;
}
